import React, { useEffect } from 'react';
import styled from 'styled-components';
import PageLayout from '../components/Layout/PageLayout';
import neaLogo from '../assets/high_quality_logo/nea_black.png';

const LogoImg = styled.img.attrs({ alt: 'Néa' })`
  height: 0.7em;
  width: auto;
  vertical-align: middle;
  display: inline-block;
  margin: 0 0.08em;
  margin-bottom: 0.11em;
`;

const HeroSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(180deg, #ffffff 0%, transparent 100%);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-family: var(--fontL);
  font-size: 3.5rem;
  font-weight: 500;
  color: var(--text-hi);
  margin-bottom: 1.5rem;
  letter-spacing: -0.03em;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  color: var(--text-hi);
  margin-bottom: 1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Lead = styled.p`
  font-size: 1.125rem;
  color: var(--text-lo);
  line-height: 1.8;
  max-width: 700px;
  margin: 0 auto;
  
  .brand {
    color: #0a7a53;
    font-weight: 600;
  }
`;

const Section = styled.section`
  padding: 3rem 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const LegalContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const IntroSection = styled.div`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 3rem;
  
  p {
    color: var(--text-lo);
    font-size: 1rem;
    line-height: 1.7;
    margin-bottom: 0.75rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    color: var(--text-hi);
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--fontL);
  font-size: 1.75rem;
  font-weight: 500;
  color: var(--text-hi);
  margin-bottom: 1.5rem;
  margin-top: 3rem;
  letter-spacing: -0.5px;
  
  &:first-of-type {
    margin-top: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-hi);
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const Text = styled.p`
  color: var(--text-lo);
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1rem;
  
  a {
    color: #0a7a53;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  strong {
    color: var(--text-hi);
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  
  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
    color: var(--text-lo);
    font-size: 1rem;
    line-height: 1.6;
    
    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: var(--text-hi);
    }
    
    strong {
      color: var(--text-hi);
    }

    a {
      color: #0a7a53;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const HighlightBox = styled.div`
  background: var(--paper-2);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
`;

const WarningBox = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin: 1.5rem 0;
  
  p {
    color: #0a7a53;
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
  }
`;

const RightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RightCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--line);
  }
`;

const RightTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-hi);
  margin-bottom: 0.5rem;
`;

const RightText = styled.p`
  color: var(--text-lo);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
`;

const ContactBox = styled.div`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 2rem;
  margin-top: 2rem;
`;

const CTASection = styled.section`
  padding: 5rem 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const CTATitle = styled.h2`
  font-family: var(--fontL);
  font-size: 2rem;
  font-weight: 500;
  color: var(--text-hi);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CTAText = styled.p`
  color: var(--text-lo);
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-family: var(--fontR);
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: background 150ms ease, transform 150ms ease;
  cursor: pointer;
  
  &.primary {
    background: var(--ink-900);
    color: var(--text-inv);
    
    &:hover {
      background: var(--ink-700);
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: transparent;
    color: var(--text-hi);
    border: 1px solid var(--line);
    
    &:hover {
      border-color: var(--text-hi);
      background: #ffffff;
    }
  }
`;

const Privacy = () => {
  useEffect(() => {
    const target = window.location.hash && document.querySelector(window.location.hash);
    if (target) target.scrollIntoView({ behavior: 'instant' });
    else window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <HeroSection>
        <Container>
          <Title>Politique de confidentialité</Title>
          <Subtitle>Protection et respect de vos données personnelles</Subtitle>
          <Lead>
            Chez <LogoImg src={neaLogo} />, la protection de vos données personnelles est notre 
            priorité absolue. Découvrez comment nous collectons, utilisons et protégeons vos informations.
          </Lead>
        </Container>
      </HeroSection>

      {/* Content */}
      <Section>
        <LegalContent>
          <IntroSection>
            <p><strong>Dernière mise à jour :</strong> 8 octobre 2026</p>
            <p>
              Cette politique de confidentialité décrit comment <LogoImg src={neaLogo} /> ("nous", "notre", "nos") collecte, 
              utilise, stocke et protège vos données personnelles lorsque vous utilisez notre service 
              d'assistance financière intelligente.
            </p>
            <p>
              <LogoImg src={neaLogo} /> est éditée par <strong>Flowdo S.à r.l.-S.</strong>, 70 rue de Merl, L-2146 Luxembourg,
              RCS B296041, responsable du traitement.
            </p>
            <p>En utilisant nos services, vous acceptez les pratiques décrites dans cette politique.</p>
          </IntroSection>

          {/* 1. Données collectées */}
          <SectionTitle>1. Données que nous collectons</SectionTitle>
          
          <SubTitle>1.1 Données d'identification</SubTitle>
          <List>
            <li>Nom et prénom</li>
            <li>Adresse email</li>
            <li>Numéro de téléphone (optionnel)</li>
            <li>Âge, genre, profession, pays, profil de risque (questionnaire d'accueil)</li>
          </List>

          <SubTitle>1.2 Données financières</SubTitle>
          <List>
            <li>Comptes et transactions bancaires, <strong>uniquement si vous connectez une banque</strong>, via Powens</li>
            <li>Budget, pots, patrimoine et objectifs que vous saisissez</li>
            <li>Vos messages avec l'assistant</li>
          </List>

          <SubTitle>1.3 Données techniques</SubTitle>
          <List>
            <li>
              Rapports de plantage de l'application (nettoyés avant envoi : ni e-mail, ni montant), modèle d'appareil,
              version du système et de l'app
            </li>
          </List>

          {/* 2. Utilisation */}
          <SectionTitle>2. Comment nous utilisons vos données</SectionTitle>

          <SubTitle>2.1 Fourniture du service</SubTitle>
          <List>
            <li>Analyse de votre situation financière</li>
            <li>Génération de recommandations par IA</li>
            <li>Suivi de vos objectifs financiers</li>
            <li>Synchronisation avec vos comptes bancaires</li>
          </List>

          <SubTitle>2.2 Amélioration du service</SubTitle>
          <List>
            <li>Corriger les bugs grâce aux rapports de plantage.</li>
          </List>

          <SubTitle>2.3 Communication</SubTitle>
          <List>
            <li>E-mails de service (code de réinitialisation du mot de passe)</li>
            <li>Réponses à vos demandes de support</li>
          </List>

          {/* 3. Base légale */}
          <SectionTitle>3. Base légale du traitement</SectionTitle>
          
          <HighlightBox>
            <SubTitle style={{ marginTop: 0 }}>Conformément au RGPD, nous traitons vos données sur les bases suivantes :</SubTitle>
            <List>
              <li><strong>Exécution du contrat :</strong> Fourniture des services <LogoImg src={neaLogo} /></li>
              <li><strong>Consentement :</strong> Connexion d'un compte bancaire</li>
              <li><strong>Intérêt légitime :</strong> Rapports de plantage, sécurité</li>
            </List>
          </HighlightBox>

          {/* 4. Partage */}
          <SectionTitle>4. Partage et transfert de données</SectionTitle>

          <SubTitle>4.1 Partenaires autorisés</SubTitle>
          <List>
            <li>
              <strong>Powens :</strong> Connexion à vos banques en lecture seule ; vos identifiants bancaires sont saisis
              chez Powens, jamais chez <LogoImg src={neaLogo} />
            </li>
            <li><strong>OVH :</strong> Hébergement et e-mails</li>
            <li><strong>Anthropic (Claude) :</strong> Réponses de l'assistant, classement des dépenses</li>
            <li><strong>Sentry (région UE) :</strong> Rapports de plantage</li>
          </List>

          <SubTitle>4.2 Transferts internationaux</SubTitle>
          <Text>
            Serveurs OVH dans l'UE (France ou Allemagne), Sentry en UE. Anthropic est aux États-Unis : les messages
            envoyés à l'assistant y sont traités.
          </Text>

          <WarningBox>
            <p>Nous ne vendons jamais vos données personnelles à des tiers.</p>
          </WarningBox>

          {/* 5. Sécurité */}
          <SectionTitle>5. Sécurité et protection</SectionTitle>

          <SubTitle>5.1 Mesures techniques</SubTitle>
          <List>
            <li>Échanges chiffrés (HTTPS)</li>
            <li>Jeton de connexion dans le trousseau sécurisé du téléphone</li>
            <li>Verrouillage de l'app par code ou biométrie, au choix</li>
          </List>

          <SubTitle>5.2 Mesures organisationnelles</SubTitle>
          <List>
            <li>Accès limité aux données selon le principe du "besoin de savoir"</li>
          </List>

          {/* 6. Vos droits */}
          <SectionTitle>6. Vos droits sur vos données</SectionTitle>

          <RightsGrid>
            <RightCard>
              <RightTitle>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{verticalAlign: 'middle', marginRight: 8}}>
                  <circle cx="9" cy="9" r="7" stroke="#3fc1ec" strokeWidth="2"/>
                  <circle cx="9" cy="9" r="3" stroke="#68d8ef" strokeWidth="1.5"/>
                  <line x1="14.5" y1="14.5" x2="18" y2="18" stroke="#87b4d9" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Droit d'accès
              </RightTitle>
              <RightText>Vous pouvez demander une copie de toutes vos données personnelles.</RightText>
            </RightCard>
            <RightCard>
              <RightTitle>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{verticalAlign: 'middle', marginRight: 8}}>
                  <rect x="3" y="14" width="8.5" height="2.5" rx="1" fill="#3fc1ec"/>
                  <rect x="8.5" y="3.5" width="8" height="11" rx="2" stroke="#3fc1ec" strokeWidth="1.5"/>
                  <rect x="11" y="6" width="3" height="1" rx="0.3" fill="#87b4d9"/>
                  <rect x="11" y="9" width="3" height="1" rx="0.3" fill="#87b4d9"/>
                </svg>
                Droit de rectification
              </RightTitle>
              <RightText>Vous pouvez corriger ou mettre à jour vos informations personnelles.</RightText>
            </RightCard>
            <RightCard>
              <RightTitle>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{verticalAlign: 'middle', marginRight: 8}}>
                  <rect x="4" y="7" width="12" height="8" rx="2" stroke="#3fc1ec" strokeWidth="1.5" fill="none"/>
                  <rect x="7" y="5" width="6" height="2" rx="1" fill="#68d8ef"/>
                  <rect x="8" y="11" width="4" height="2" rx="0.7" fill="#87b4d9"/>
                  <rect x="6" y="2.5" width="8" height="2" rx="1" fill="#c0eaff"/>
                </svg>
                Droit à l'effacement
              </RightTitle>
              <RightText>Vous pouvez demander la suppression de vos données (droit à l'oubli).</RightText>
            </RightCard>
            <RightCard>
              <RightTitle>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{verticalAlign: 'middle', marginRight: 8}}>
                  <rect x="4" y="7" width="12" height="6" rx="2" stroke="#3fc1ec" strokeWidth="1.5" fill="none"/>
                  <rect x="6" y="10.5" width="8" height="2" rx="1" fill="#68d8ef"/>
                  <rect x="7" y="3.5" width="6" height="3" rx="1.5" fill="#c0eaff"/>
                  <rect x="8.5" y="8" width="3" height="2" rx="0.7" fill="#87b4d9"/>
                </svg>
                Droit à la limitation
              </RightTitle>
              <RightText>Vous pouvez demander la suspension du traitement de vos données.</RightText>
            </RightCard>
            <RightCard>
              <RightTitle>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{verticalAlign: 'middle', marginRight: 8}}>
                  <rect x="4" y="5" width="12" height="10" rx="2" stroke="#3fc1ec" strokeWidth="1.5" fill="none"/>
                  <path d="M8 13l4-3.5L8 6" stroke="#68d8ef" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="15" y="8" width="1.5" height="4" rx="0.7" fill="#87b4d9"/>
                </svg>
                Droit à la portabilité
              </RightTitle>
              <RightText>Vous pouvez récupérer vos données dans un format structuré.</RightText>
            </RightCard>
            <RightCard>
              <RightTitle>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{verticalAlign: 'middle', marginRight: 8}}>
                  <circle cx="10" cy="10" r="8" stroke="#3fc1ec" strokeWidth="2"/>
                  <line x1="6" y1="6" x2="14" y2="14" stroke="#68d8ef" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="14" y1="6" x2="6" y2="14" stroke="#87b4d9" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Droit d'opposition
              </RightTitle>
              <RightText>Vous pouvez vous opposer au traitement pour motifs légitimes.</RightText>
            </RightCard>
          </RightsGrid>

          <ContactBox>
            <SubTitle style={{ marginTop: 0 }}>Comment exercer vos droits ?</SubTitle>
            <Text>
              Pour exercer vos droits, contactez-nous à : <strong><a href="mailto:info@nea.lu">info@nea.lu</a></strong>
            </Text>
            <Text>
              Nous répondrons à votre demande sous un mois.
            </Text>
            <Text style={{ marginBottom: 0 }}>
              Vous pouvez saisir la CNPD (<a href="https://cnpd.public.lu" target="_blank" rel="noopener noreferrer">cnpd.public.lu</a>).
            </Text>
          </ContactBox>

          {/* 7. Cookies */}
          <SectionTitle id="cookies">7. Cookies et technologies similaires</SectionTitle>
          <Text>
            L'application n'utilise ni cookie ni traceur. Le site try-nea.com n'utilise aucun cookie de mesure ni de
            publicité.
          </Text>

          {/* 8. Conservation */}
          <SectionTitle>8. Durée de conservation</SectionTitle>
          <List>
            <li>
              <strong>Vos données :</strong> Gardées tant que votre compte existe et supprimées quand vous le supprimez
            </li>
            <li><strong>Rapports de plantage :</strong> 30 jours</li>
          </List>

          <SubTitle id="suppression">Supprimer votre compte</SubTitle>
          <List>
            <li>
              <strong>Dans l'application :</strong> Menu utilisateur (icône de profil) → « Supprimer le compte », puis
              confirmer. Le compte et les données associées sont supprimés de nos serveurs, puis de votre téléphone.
            </li>
            <li>
              <strong>Sans l'application</strong> (app désinstallée, téléphone perdu) : Écrivez
              à <a href="mailto:info@nea.lu">info@nea.lu</a> depuis l'adresse e-mail de votre compte, ou utilisez
              le <a href="/support">formulaire de support</a>, avec l'objet « Suppression de compte ».
            </li>
          </List>

          {/* 9. Modifications */}
          <SectionTitle>9. Modifications de cette politique</SectionTitle>
          <Text>
            Nous pouvons modifier cette politique de confidentialité de temps en temps. 
            Toute modification importante vous sera notifiée par email ou via notre application.
          </Text>
          <Text>
            La version la plus récente est toujours disponible sur cette page avec 
            la date de dernière mise à jour en haut du document.
          </Text>

          {/* 10. Contact */}
          <SectionTitle>10. Nous contacter</SectionTitle>
          <ContactBox>
            <SubTitle style={{ marginTop: 0 }}>Contact pour vos données</SubTitle>
            <Text>
              <strong>Email :</strong> <a href="mailto:info@nea.lu">info@nea.lu</a>
            </Text>
          </ContactBox>
        </LegalContent>
      </Section>

      {/* CTA */}
      <CTASection>
        <Container>
          <CTATitle>Des questions sur vos données ?</CTATitle>
          <CTAText>Notre équipe est à votre disposition pour répondre à toutes vos questions.</CTAText>
          <ButtonGroup>
            <Button href="mailto:info@nea.lu" className="primary">Nous contacter</Button>
            <Button href="/about" className="secondary">En savoir plus sur <LogoImg src={neaLogo} /></Button>
          </ButtonGroup>
        </Container>
      </CTASection>
    </PageLayout>
  );
};

export default Privacy;
